// PII patterns
const PHONE_REGEX = /\b(\+?91[\s-]?)?[6-9]\d{9}\b/g;
const EMAIL_REGEX = /[\w.-]+@[\w.-]+\.\w+/g;
const CARD_REGEX = /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g;

// URL query params that carry PII when paired with sensitive `type` values
const SENSITIVE_SEARCH_TYPES = [
  'user_number',
  'user_email',
  'user_id',
  'email_id',
  'phone',
];

// Keys to strip from Jumbo bridge payloads before forwarding to Datadog
const PII_EVENT_KEYS = [
  'customer_id',
  'customerId',
  'user_number',
  'user_email',
  'valet_number',
  'valet_name',
  'merchant_name',
  'phone',
  'email',
  'name',
  'email_id',
  'phone_string',
];

/**
 * Scrub phone numbers, emails, and card numbers from free text.
 */
export const scrubText = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(PHONE_REGEX, '[PHONE_REDACTED]')
    .replace(EMAIL_REGEX, '[EMAIL_REDACTED]')
    .replace(CARD_REGEX, '[CARD_REDACTED]');
};

/**
 * Redact the `value` query param when the `type` param indicates PII.
 * e.g. ?type=user_number&value=9876543210 → ?type=user_number&value=[REDACTED]
 */
export const scrubUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  try {
    const urlObj = new URL(url);
    const type = urlObj.searchParams.get('type');
    if (type && SENSITIVE_SEARCH_TYPES.includes(type)) {
      urlObj.searchParams.set('value', '[REDACTED]');
    }
    // Scrub value even without type, if the param name itself is sensitive
    for (const param of SENSITIVE_SEARCH_TYPES) {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[REDACTED]');
      }
    }
    return urlObj.toString();
  } catch {
    return scrubText(url);
  }
};

/**
 * Strip PII keys from a Jumbo event payload before sending to Datadog.
 * Returns a new object — does not mutate the input.
 */
export const stripPiiFromEventPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;

  const cleaned = {};
  for (const [key, value] of Object.entries(payload)) {
    if (PII_EVENT_KEYS.includes(key)) continue;

    if (key === 'feature_meta' && value && typeof value === 'object') {
      const cleanedMeta = {};
      for (const [metaKey, metaValue] of Object.entries(value)) {
        if (PII_EVENT_KEYS.includes(metaKey)) continue;
        cleanedMeta[metaKey] =
          typeof metaValue === 'string' ? scrubText(metaValue) : metaValue;
      }
      cleaned[key] = cleanedMeta;
    } else {
      cleaned[key] = typeof value === 'string' ? scrubText(value) : value;
    }
  }

  return cleaned;
};

/**
 * Datadog RUM beforeSend callback.
 * Scrubs PII from all event types before they leave the browser.
 */
export const ddBeforeSend = (event) => {
  // View events: scrub URL and referrer
  if (event.view) {
    if (event.view.url) event.view.url = scrubUrl(event.view.url);
    if (event.view.referrer)
      event.view.referrer = scrubUrl(event.view.referrer);
  }

  // Error events: scrub message, stack, and resource URL
  if (event.type === 'error') {
    if (event.error.message)
      event.error.message = scrubText(event.error.message);
    if (event.error.stack) event.error.stack = scrubText(event.error.stack);
    if (event.error.resource?.url)
      event.error.resource.url = scrubUrl(event.error.resource.url);
  }

  // Resource events: scrub URL
  if (event.type === 'resource' && event.resource?.url) {
    event.resource.url = scrubUrl(event.resource.url);
  }

  // Action events: scrub target name as safety net
  if (event.type === 'action' && event.action?.target?.name) {
    event.action.target.name = scrubText(event.action.target.name);
  }

  return true;
};
